import React, { useEffect } from 'react';
import type { PropsWithChildren } from 'react';
import { StyledEngineProvider, ThemeProvider } from '@mui/material';
import getThemeByColorMode, {
  ColorModeProvider,
  useColorMode,
} from '@/styles/theme';
import { SnackBarProvider } from 'pragmatic-ui';
import { QueryClientProvider } from '@tanstack/react-query';
import { ErrorBoundary } from 'react-error-boundary';
import { HelmetProvider } from 'react-helmet-async';
import { ReactQueryDevtools } from '@tanstack/react-query-devtools';
import { queryClient } from '@/lib/react-query';
import { CircularProgress } from '@mui/material';
import { UserProvider } from '@/context/user';
import ErrorFallback from './pages/admin/ErrorFallback';
import { Notifications } from './components/Notifications';
import { CssBaseline } from '@mui/material';

const ThemedApp = ({ children }: PropsWithChildren) => {
  const { mode, toggleColorMode } = useColorMode();

  const isAdminPage = () => {
    if (typeof window === 'undefined') return false;
    return window.location.pathname.includes('/admin');
  };

  // Force dark mode for admin pages by updating the context
  useEffect(() => {
    if (typeof window === 'undefined') return;

    if (isAdminPage() && mode !== 'dark') {
      toggleColorMode();
    }

    const handleRouteChange = () => {
      if (isAdminPage() && mode !== 'dark') {
        toggleColorMode();
      }
    };

    window.addEventListener('popstate', handleRouteChange);

    return () => {
      window.removeEventListener('popstate', handleRouteChange);
    };
  }, [mode, toggleColorMode]);

  const effectiveMode = isAdminPage() ? 'dark' : mode;
  const theme = getThemeByColorMode(effectiveMode);

  return (
    <ThemeProvider theme={theme}>
      <CssBaseline>
        <React.Suspense fallback={<CircularProgress />}>
          <ErrorBoundary fallback={<ErrorFallback />}>
            <HelmetProvider>
              <SnackBarProvider>
                <QueryClientProvider client={queryClient}>
                  <UserProvider>
                    <Notifications>
                      {import.meta.env.DEV && <ReactQueryDevtools />}
                      {children}
                    </Notifications>
                  </UserProvider>
                </QueryClientProvider>
              </SnackBarProvider>
            </HelmetProvider>
          </ErrorBoundary>
        </React.Suspense>
      </CssBaseline>
    </ThemeProvider>
  );
};

export default function AppProvider({ children }: PropsWithChildren) {
  return (
    <StyledEngineProvider injectFirst>
      <ColorModeProvider>
        <ThemedApp>{children}</ThemedApp>
      </ColorModeProvider>
    </StyledEngineProvider>
  );
}
